import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "textarea", "error"]

  connect() {
    this.initializeEditor()
  }

  disconnect() {
    this.view?.destroy()
  }

  async initializeEditor() {
    this.editorTarget.innerHTML = '<div class="text-gray-500">Loading JSON editor...</div>'

    try {
      const [
        { EditorView, lineNumbers, highlightSpecialChars, drawSelection, keymap },
        { EditorState },
        { defaultKeymap, history, historyKeymap },
        { syntaxHighlighting, defaultHighlightStyle, bracketMatching },
        { json, jsonParseLinter },
        { linter, lintGutter }
      ] = await Promise.all([
        import("@codemirror/view"),
        import("@codemirror/state"),
        import("@codemirror/commands"),
        import("@codemirror/language"),
        import("@codemirror/lang-json"),
        import("@codemirror/lint")
      ])

      const initialDoc = this.textareaTarget.value || ""
      this.editorTarget.innerHTML = ""

      this.view = new EditorView({
        doc: initialDoc,
        parent: this.editorTarget,
        extensions: [
          lineNumbers(),
          highlightSpecialChars(),
          history(),
          drawSelection(),
          EditorState.allowMultipleSelections.of(true),
          syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
          bracketMatching(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          json(),
          linter(jsonParseLinter()),
          lintGutter(),
          EditorView.lineWrapping,
          EditorView.theme({
            "&": {
              fontSize: "14px",
              border: "1px solid #e5e7eb",
              borderRadius: "6px"
            },
            ".cm-content": {
              padding: "12px",
              minHeight: "200px"
            },
            ".cm-focused": {
              outline: "2px solid #3b82f6"
            }
          }),
          EditorView.updateListener.of(update => {
            if (update.docChanged) {
              const content = update.state.doc.toString()
              this.textareaTarget.value = content
              this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))
              this.validateJson(content)
            }
          })
        ]
      })

      this.textareaTarget.style.display = "none"
      this.validateJson(initialDoc)

    } catch (err) {
      console.error("Failed to load JSON editor:", err)
      this.editorTarget.innerHTML = '<div class="text-red-600">Failed to load editor</div>'
      this.textareaTarget.style.display = "block"
    }
  }

  validateJson(content) {
    if (!this.hasErrorTarget) return
    try {
      if (content.trim()) JSON.parse(content)
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    } catch (e) {
      this.errorTarget.textContent = `JSON解析エラー: ${e.message}`
      this.errorTarget.classList.remove("hidden")
    }
  }

  formatJson(event) {
    event?.preventDefault()
    if (!this.view) return
    try {
      const raw = this.view.state.doc.toString()
      const formatted = JSON.stringify(JSON.parse(raw), null, 2)
      this.view.dispatch({
        changes: { from: 0, to: this.view.state.doc.length, insert: formatted }
      })
    } catch (_) {}
  }
}